<template>
  <div ref="container" class="plv-ia-container plv-ia-answer">
    <!-- tab -->
    <div class="plv-ia-answer-tabs__header">
      <span
        :class="['plv-ia-answer-tabs__active-bar', {
          'plv-ia-answer-tabs__active-bar--history': curTab === 'history',
          'plv-ia-answer-tabs__active-bar--log': curTab === 'log',
        }]"
      ></span>
      <span
        :class="['plv-ia-answer-tabs__nav', {
          'plv-ia-answer-tabs__nav--active': curTab === 'main'
        }]"
        @click="changeCurTab('main')"
      >
        {{ t('plvIA.answerCard.answerSheet') }}
      </span>
      <span
        :class="['plv-ia-answer-tabs__nav', {
          'plv-ia-answer-tabs__nav--active': curTab === 'history'
        }]"
        @click="changeCurTab('history')"
      >
        {{ t('plvIA.answerCard.questionBank') }}
      </span>
      <span
        v-if="showLog"
        :class="['plv-ia-answer-tabs__nav', {
          'plv-ia-answer-tabs__nav--active': curTab === 'log'
        }]"
        @click="changeCurTab('log')"
      >
        日志打印
      </span>
      <span v-if="showLog" class="plv-ia-answer-version">version: 2021-05-07</span>
    </div>

    <!-- 答题卡 pane -->
    <div
      :class="['plv-ia-answer-tabs__pane', {
        'plv-ia-answer-tabs__pane--active': curTab === 'main',
      }]"
    >
      <div class="plv-ia-answer-main">
        <div class="plv-ia-answer-main__left">
          <sidebar
            :list="questionList"
            :cur-question-id="curQuestionId"
            :ongoing-question-id="ongoingQuestionId"
            :cur-template-id="curTemplateId"
            :is-creating="isCreating"
            @download="handleDownload"
            @create="handleCreate"
            @upload="handleUpload(curTemplateId, false)"
            @change="handleChangeQuestion"
          />
        </div>

        <div class="plv-ia-answer-main__right">
          <question-edit
            v-if="isEditing"
            :question="curQuestion"
            :channel-setting="channelSetting"
            @save="handleSaveQuestion"
            @send="handleSendQuestion"
            @cancel="handleEditCancel"
          />
          <question-content
            v-else
            :question="curQuestion"
            :option-results="optionResults"
            :statistics="statistics"
            :submitted-count="submittedCount"
            :is-ongoing="ongoingQuestionId === curQuestionId"
            :sent-list="sentList"
            :channel-setting="channelSetting"
            :countdown="countdown"
            :is-stopping="isStopping"
            @edit="handleEdit"
            @send-result="handleSendResult"
            @stop="handleStop"
            @start="startQuestion"
            @continue="handleContinueQuickAnswer"
            @delete="handleQuestionDelete"
          />
        </div>
      </div>
    </div>

    <!-- 题库 pane -->
    <div
      :class="['plv-ia-answer-tabs__pane', {
        'plv-ia-answer-tabs__pane--active': curTab === 'history'
      }]"
    >
      <question-bank
        :list="bankList"
        @download="handleDownload"
        @import="handleBankImport"
        @delete="handleBankDelete"
        @upload="handleUpload('', $event)"
      />
    </div>

    <!-- 日志 pane -->
    <div
      v-if="showLog"
      :class="['plv-ia-answer-tabs__pane', {
        'plv-ia-answer-tabs__pane--active': curTab === 'log'
      }]"
    >
      <div class="plv-ia-answer-log">
        <button class="plv-ia-btn-new plv-ia-btn-orange" @click="logList = []">清空日志</button>
        <div class="plv-ia-answer-log_list">
          <p v-for="(item, index) in logList" :key="`${item.ts}-${index}`">{{ item.ts }}: {{ item.content }}</p>
        </div>
      </div>
    </div>

    <div v-show="false" @change="handleFileBtnChange">
      <input
        ref="pickFile"
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
      />
    </div>

    <!-- 确认删除题库 -->
    <confirm-delete
      :show="showDeleteConfirm"
      @hide="showDeleteConfirm = false"
      @delete="handleBankDeleteConfirm(delTmplId)"
    />

    <!-- 确认删除答题卡 -->
    <confirm-delete-question
      :show="showDeleteQuestionConfirm"
      @hide="showDeleteQuestionConfirm = false"
      @delete="handleQuestionDeleteConfirm"
    />

    <!-- 确认放弃编辑 -->
    <confirm-discard
      :show="showDiscardConfirm"
      @hide="showDiscardConfirm = false"
      @ensure="handleDiscardConfirm"
    />

    <!-- 确认覆盖重名题库 -->
    <confirm-cover
      :show="showCoverConfirm"
      @hide="showCoverConfirm = false"
      @ensure="handleCoverConfirm"
    />
  </div>
</template>

<script type="text/babel">
import { AnswerCard } from '@polyv/interactions-launch-sdk';
import Locale from '@/mixins/locale';
import QuestionBank from './QuestionBank';
import ConfirmDelete from './ConfirmDelete';
import ConfirmDeleteQuestion from './ConfirmDeleteQuestion';
import Sidebar from './Sidebar';
import QuestionContent from './QuestionContent';
import QuestionEdit from './QuestionEdit';
import ConfirmDiscard from './ConfirmDiscard';
import ConfirmCover from './ConfirmCover';
import commonMixin from '../app-mixin';

export default {
  name: 'AnswerCard',

  components: {
    QuestionBank,
    ConfirmDelete,
    ConfirmDeleteQuestion,
    Sidebar,
    QuestionContent,
    QuestionEdit,
    ConfirmDiscard,
    ConfirmCover,
  },

  mixins: [commonMixin, Locale],

  props: {
    /** 频道自定义设置。 */
    channelSetting: {
      type: Object,
      default() {
        return {
          // 是否默认启用定时发送
          defaultLimitTimeAnswer: false,
          // 答题卡题干和选项内容是否可以为空
          answerContentBlankEnabled: false,
        };
      }
    },
    /** 是否显示日志 tab */
    showLog: {
      default: false,
      type: Boolean,
    },
    /** 答题卡 SDK 实例 */
    core: {
      type: Object,
      default: null,
    },
    /** 需要显示结果的题目 id */
    resultId: {
      type: String,
      default: '',
    },
  },

  data() {
    const answerData = this.getDefaultQuicklyAnswerModel();
    const questionList = [answerData];

    return {
      NETWORK_ERR: this.t('plvIA.common.networkErr'),
      curTab: 'main',

      bankList: null, // 题库列表
      delTmplId: '',
      showDeleteConfirm: false,

      nextQuestionId: '',
      showDiscardConfirm: false,
      showDeleteQuestionConfirm: false,

      quicklyAnswer: answerData,
      questionList, // 问题列表
      ongoingQuestionId: '', // 答题中的题目id
      ongoingResults: [],
      curQuestionId: 'quickQuestion', // 当前选中的题目id
      statistics: null,
      submittedCount: 0, // 提交人数
      isEditing: true,
      curQuestion: questionList[0],
      curTemplateId: '',
      sentList: [], // 已发送结果列表
      countdown: 0, // 倒计时
      file: null, // 待上传文件
      showCoverConfirm: false,

      // 防止重复提交表单
      isStopping: false,
      isStarting: false,
      isDeleting: false,
      isSaving: false,

      // 如果是因为收到socket消息而打开弹窗，需要跳转到指定id的题目
      showResultId: '',
      // 是否已调用initData
      isInited: false,

      // 日志列表
      logList: []
    };
  },

  computed: {
    isCreating() {
      return this.isEditing && this.curQuestionId !== 'quickQuestion';
    },

    optionResults() {
      return this.curQuestionId === this.ongoingQuestionId ? this.ongoingResults : [];
    },

    usingBankName() {
      for (let index = 0; index < this.bankList.length; index++) {
        const item = this.bankList[index];
        if (item.status === 'used') return item.name;
      }
      return '';
    },
  },

  watch: {
    resultId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.showResult(val);
        }
      }
    },

    curQuestionId(val) {
      this.statistics = null;
      let curQuestion = null;

      if (val === '') {
        curQuestion = this.getNewQuestion();
        this.isEditing = true;
      } else if (val === 'quickQuestion') {
        curQuestion = this.questionList[0];
        this.isEditing = true;
      } else {
        curQuestion = this.questionList.filter(item => item.questionId === val)[0] || {};
        this.isEditing = false;
      }
      this.curQuestion = curQuestion;

      if (curQuestion.status === 'send' && this.ongoingQuestionId !== val) {
        this.getStatistics(val);
      }
    },
  },

  beforeDestroy() {
    this.unbindCoreListeners();
    this.clearCountdownTimer();
  },

  methods: {
    async afterCoreInited() {
      this.bindCoreListeners();
      this.updateBankList();
      await this.initData();
    },
    log(content) {
      const ts = Date.now();
      this.logList.push({
        ts,
        content,
      });
    },

    async initData() {
      await Promise.all([this.updateQuestionList(), this.getQuestionState()]);
      this.isInited = true;

      if (this.showResultId) {
        this.handleChangeQuestion(this.showResultId, true);
      }
    },

    getNewQuestion() {
      return {
        name: this.t('plvIA.answerCard.createQuestion'),
        questionId: '',
        type: 'R',
        itemType: 0, // 使用itemType（Number类型）区分普通答题卡（0）和问答（1）
        answer: '',
        option1: '',
        option2: '',
      };
    },

    getDefaultQuicklyAnswerModel() {
      return {
        name: this.t('plvIA.answerCard.quickQuestion'),
        questionId: 'quickQuestion',
        type: 'R',
        itemType: 1,
        answer: 'A',
      };
    },

    changeCurTab(tabType) {
      if (tabType === 'history' && this.isCreating) {
        this.showDiscardConfirm = true;
        this.nextQuestionId = 'quickQuestion';
        this.nextTab = 'history';
        return;
      }
      this.curTab = tabType;
    },

    // 绑定 core 事件监听
    bindCoreListeners() {
      this.core
        .on('start', this.handleStartMsg)
        .on('stop', this.handleStopMsg)
        .on('answer', this.handleAnswerMsg)
        .on('show-result', this.handelShowResultMsg);
    },

    // 取消绑定 core 事件监听
    unbindCoreListeners() {
      this.core
        .off('start', this.handleStartMsg)
        .off('stop', this.handleStopMsg)
        .off('answer', this.handleAnswerMsg)
        .off('show-result', this.handelShowResultMsg);
    },

    handelShowResultMsg({ questionId }) {
      this.showResult(questionId);
    },

    // 开始答题。
    handleStartMsg({ question, setting }) {
      this.playSoundEffects();
      this.handleOngoingQuestionMessage({ question, setting });

      // 开始新的答题，清空统计信息
      this.ongoingResults = [];
      this.submittedCount = 0;
    },

    // 获取答题卡剩余时间。
    async getRemainingTime() {
      const remainningTime = await this.core.getRemainingTime();
      this.setCountdown(remainningTime);
    },

    clearCountdownTimer() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },

    // 设置限时答题的倒计时
    setCountdown(duration) {
      if (!duration) return;

      duration = parseInt(duration);
      this.countdown = duration;
      this.clearCountdownTimer();
      this.countdownTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown === 0) {
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
        }
      }, 1000);
    },

    // 收到观众回答信息。
    handleAnswerMsg({ questionId, answer }) {
      if (questionId !== this.ongoingQuestionId) return;
      const ongoingResults = [...this.ongoingResults];
      const charList = answer.split('');
      for (let index = 0; index < charList.length; index++) {
        const char = charList[index];
        const optionIndex = char.charCodeAt() - 65;
        ongoingResults[optionIndex] = (ongoingResults[optionIndex] || 0) + 1;
      }
      this.ongoingResults = ongoingResults;
      this.submittedCount++;
    },

    // 停止答题。
    handleStopMsg({ questionId }) {
      this.ongoingResults = [];
      if (this.ongoingQuestionId === this.curQuestionId) {
        this.$set(this.curQuestion, 'status', 'send');
      }
      this.ongoingQuestionId = '';
      if (this.curQuestionId === questionId) {
        this.getStatistics(questionId);
      }
      this.updateQuestionList();
    },

    // 接收到新的答题消息（从聊天室接口获取或从socket消息获取）
    handleOngoingQuestionMessage({ question, setting }) {
      const { questionId, isQuickQuestion } = question;
      // if (this.ongoingQuestionId === questionId) {
      //   // 已经设置过答题中的题目，不需要重复设置
      //   return;
      // }
      const delIdIndex = this.sentList.indexOf(questionId);
      this.sentList.splice(delIdIndex, 1);
      const newQuestion = {
        questionId,
        name: question.name,
        type: question.type,
        itemType: isQuickQuestion ? 1 : 0,
        answer: question.answer || '',
        duration: setting.duration,
      };
      question.options.forEach((option, optionIndex) => {
        newQuestion[`option${optionIndex + 1}`] = option;
      });
      question.tips.forEach((tips, tipsIndex) => {
        newQuestion[`tips${tipsIndex + 1}`] = tips;
      });
      this.updateOngoingQuestion(newQuestion);
      if (isQuickQuestion && parseInt(this.curQuestion.itemType) === 1) {
        this.curQuestionId = questionId;
      }
      if (this.curQuestionId === questionId) {
        this.curQuestion = newQuestion;
        this.statistics = null;
      }

      this.ongoingQuestionId = questionId;
      this.moveOngoingQuestion(questionId);

      if (setting.duration) {
        this.getRemainingTime();
      }
    },

    updateOngoingQuestion(questionData) {
      const { questionId } = questionData;
      if (questionData.itemType === 1) {
        this.$set(this.questionList, 0, questionData);
        if (this.curQuestionId === 'quickQuestion') {
          this.curQuestionId = questionId;
        }
      } else {
        let existIndex = -1;
        for (let index = 0; index < this.questionList.length; index++) {
          const item = this.questionList[index];
          if (item.questionId === questionId) {
            existIndex = index;
            break;
          }
        }
        if (existIndex > -1) {
          this.$set(this.questionList, existIndex, questionData);
        } else {
          this.questionList.splice(1, 0, questionData);
        }
      }
    },

    moveOngoingQuestion(ongoingQuestionId) {
      if (!ongoingQuestionId) return;
      let pos = 0;
      for (let index = 0; index < this.questionList.length; index++) {
        const question = this.questionList[index];
        if (question.questionId === ongoingQuestionId) {
          pos = index;
          break;
        }
      }
      // 第一项是快速问答，不需要移动位置；以及第二项也不需要移动
      if (pos > 1) {
        let newPos = 1;
        if (this.isEditing && !this.curQuestionId) { // 正在新建题目
          newPos = 2;
        }
        const question = this.questionList.splice(pos, 1)[0];
        this.questionList.splice(newPos, 0, question);
      }
    },

    // 快速问答中的“继续发题”
    handleContinueQuickAnswer() {
      this.curQuestionId = 'quickQuestion';
      this.$set(this.questionList, 0, this.getDefaultQuicklyAnswerModel());
    },

    // 删除答题卡
    handleQuestionDelete() {
      this.showDeleteQuestionConfirm = true;
    },

    // 应用（导入）题库
    handleBankImport(templateId) {
      if (this.ongoingQuestionId) {
        this.$iaMessage.default(this.t('plvIA.answerCard.ongoingTips'));
        return;
      }

      this.importBank(templateId);
    },

    // 删除题库
    handleBankDelete(tmplId) {
      this.delTmplId = tmplId;
      this.showDeleteConfirm = true;
    },

    // 确认删除题库
    handleBankDeleteConfirm() {
      this.deleteBank(this.delTmplId);
      this.delTmplId = '';
      this.showDeleteConfirm = false;
    },

    // 确认删除答题卡
    async handleQuestionDeleteConfirm() {
      if (this.isDeleting) return;
      this.isDeleting = true;

      try {
        await this.core.deleteQuestion(this.curQuestionId);
        this.$iaMessage.default(this.t('plvIA.common.deleteSuccessfullyTips'));
        await this.updateQuestionList();
        this.curQuestionId = 'quickQuestion';
      } catch (error) {
        this.$iaMessage.default(error.message || this.NETWORK_ERR);
      } finally {
        this.isDeleting = false;
      }
      this.showDeleteQuestionConfirm = false;
    },

    // 确认放弃编辑
    handleDiscardConfirm() {
      // 从“新建题目”切换到其它
      if (this.curQuestionId === '') {
        this.questionList.splice(1, 1);
      }
      this.curQuestionId = this.nextQuestionId;
      this.nextQuestionId = '';
      if (this.nextTab) {
        this.curTab = this.nextTab;
        this.nextTab = '';
      }
      this.showDiscardConfirm = false;
    },

    // 下载题库或模板。
    handleDownload(templateId) {
      this.core.downloadTemplate(templateId);
    },

    // 新建题目
    handleCreate() {
      this.curQuestionId = '';
      this.questionList.splice(1, 0, this.getNewQuestion());
    },

    isDuplicatedBank(filename) {
      let flag = false;
      for (let index = 0; index < this.bankList.length; index++) {
        const item = this.bankList[index];
        if (item.name === filename) {
          flag = true;
          break;
        }
      }
      return flag;
    },

    // 上传
    handleUpload(templateId = '', isReupload) {
      const input = this.$refs.pickFile;
      input.setAttribute('data-templateid', templateId);
      input.setAttribute('data-isreupload', isReupload);
      input.value = '';
      input.click();
    },

    // 选中文件进行上传
    handleFileBtnChange() {
      const input = this.$refs.pickFile;
      const file = input.files[0];
      this.file = file;

      const templateId = input.getAttribute('data-templateid');
      const isReupload = input.getAttribute('data-isreupload') === 'true';

      if (!templateId && !isReupload) {
        const filename = file.name.replace(/(.+)\..+$/, '$1');
        if (filename === '默认题库') {
          this.$iaMessage.default(this.t('plvIA.answerCard.bankExistedTips'));
          return;
        }
        const isDuplicatedBank = this.isDuplicatedBank(filename);
        if (isDuplicatedBank) {
          if (this.usingBankName === filename) {
            this.$iaMessage.default(this.t('plvIA.answerCard.bankUsingNoCoverTips'));
            return;
          }
          this.showCoverConfirm = true;
          return;
        }
      }
      this.uploadTemplate(templateId);
    },

    handleCoverConfirm() {
      this.showCoverConfirm = false;
      this.uploadTemplate();
    },

    async uploadTemplate(templateId) {
      try {
        await this.core.uploadTemplate(this.file, templateId);
        if (!templateId) {
          this.updateBankList();
        }
        this.updateQuestionList();
        this.$iaMessage.default(this.t('plvIA.common.uploadSuccessfullyTips'));
      } catch (error) {
        this.$iaMessage.default(error.message || this.NETWORK_ERR);
      } finally {
        this.file = null;
      }
    },

    // 切换题目
    handleChangeQuestion(id, isFromSendResult = false) {
      if (this.curQuestion.itemType === 0 && this.isEditing) {
        if (isFromSendResult) return;
        this.nextQuestionId = id;
        this.showDiscardConfirm = true;
        return;
      } else if (this.curQuestion.itemType === 1 && this.curQuestion.questionId !== this.ongoingQuestionId) {
        // 从已完成答题的快速问答切换到其它题目时需要清空快速问答的内容
        this.$set(this.questionList, 0, this.getDefaultQuicklyAnswerModel());
      }
      this.curQuestionId = id;
    },

    handleEdit() {
      this.isEditing = true;
    },

    // 取消编辑
    handleEditCancel() {
      if (this.curQuestionId === '') {
        this.questionList.splice(1, 1);
        this.curQuestionId = this.questionList[0].questionId;
      }
      this.isEditing = false;
    },

    // 获取题库列表。
    async updateBankList() {
      try {
        // 不做分页，每次取1000条数据
        const { list } = await this.core.getTemplateList({ page: 1, pageSize: 1000 });
        this.bankList = list;
      } catch (error) {
        this.$iaMessage.default(error.message || this.NETWORK_ERR);
      }
    },

    // 删除题库。
    async deleteBank(templateId) {
      try {
        await this.core.deleteTemplate(templateId);
        this.$iaMessage.default(this.t('plvIA.common.deleteSuccessfullyTips'));
        this.updateBankList();
      } catch (error) {
        this.$iaMessage.default(error.message || this.NETWORK_ERR);
      }
    },

    // 应用题库。
    async importBank(templateId) {
      try {
        await this.core.applyTemplate(templateId);
        this.$iaMessage.default(this.t('plvIA.common.importSuccessTips'));
        await this.updateQuestionList();
        this.curQuestionId = 'quickQuestion';
        this.sentList = [];
        this.statistics = null;
        this.curTab = 'main';
        this.updateBankList();
      } catch (error) {
        this.$iaMessage.default(error.message || this.NETWORK_ERR);
      }
    },

    // 获取当前正在使用的的题目列表。
    async updateQuestionList() {
      try {
        const { templateId, questions } = await this.core.getQuestionList();
        const preList = [this.questionList[0]];
        if (this.isCreating) {
          preList.push(this.curQuestion);
        }
        const formatedQuestions = questions.map((question) => {
          return {
            name: question.name,
            type: question.type,
            itemType: question.isQuickQuestion ? 1 : 0,
            answer: question.answer,
            questionId: question.questionId,
            status: question.status,
            ...AnswerCard.formatOptionsAndTipsToObject({ options: question.options, tips: question.tips }),
          };
        });
        this.questionList = [...preList, ...(formatedQuestions || [])];
        if (templateId) {
          this.curTemplateId = templateId;
        }
        this.moveOngoingQuestion(this.ongoingQuestionId);
      } catch (error) {
        this.$iaMessage.default(error.message || this.NETWORK_ERR);
      }
    },

    // 获取房间内答题情况。
    async getQuestionState() {
      try {
        const { isOngoing, question, setting, statistics } = await this.core.getOngoingStatus();

        if (!isOngoing) return;
        this.handleOngoingQuestionMessage({ question, setting });

        // 设置统计信息
        this.ongoingResults = statistics.options.map(item => item.count);
        this.submittedCount = statistics.total;
      } catch (error) {
        this.$iaMessage.default(error.message || this.NETWORK_ERR);
      }
    },

    // 获取答题统计数据。
    async getStatistics(questionId) {
      try {
        const statistics = await this.core.getQuestionStatistics(questionId);
        statistics.options = statistics.options.map((item, index) => {
          return {
            ...item,
            title: this.curQuestion[`option${index + 1}`],
          };
        });
        this.statistics = statistics;
      } catch (error) {
        this.$iaMessage.default(error.message || this.NETWORK_ERR);
      } finally {
        this.isStopping = false;
      }
    },

    // 发送答题结果。
    async handleSendResult() {
      try {
        const question = this.curQuestion;
        await this.core.sendResult({
          question: {
            questionId: question.questionId,
            name: question.name,
            type: question.type,
            isQuickQuestion: question.itemType === 1,
            answer: question.answer,
          },
          statistics: this.statistics,
        });
        this.$iaMessage.default(this.t('plvIA.common.sentSuccessfullyTips'));
      } catch (error) {
        this.$iaMessage.default(error.message || this.NETWORK_ERR);
      }
    },

    // 结束答题。
    async handleStop() {
      if (this.isStopping) return;
      this.isStopping = true;

      const questionId = this.curQuestionId;
      try {
        await this.core.stop(questionId);
      } catch (error) {
        this.$iaMessage.default(error.message || this.NETWORK_ERR);
      }
    },

    async handleSendQuestion(question) {
      if (this.ongoingQuestionId) {
        this.$iaMessage.default(this.t('plvIA.answerCard.ongoingTips'));
        return;
      }

      try {
        const data = JSON.parse(JSON.stringify(question));
        const res = await this.addQuestion(data);
        data.questionId = res;
        this.startQuestion(data);
      } catch (error) {
        this.$iaMessage.default(error.message || this.NETWORK_ERR);
      }
    },

    async handleSaveQuestion(question) {
      if (this.isSaving) return;
      this.isSaving = true;

      try {
        const res = await this.addQuestion(question);
        this.isEditing = false;
        await this.updateQuestionList();
        this.curQuestionId = res;
        this.curQuestion = Object.assign({}, this.questionList[1], {
          status: 'draft'
        });
      } catch (error) {
        this.$iaMessage.default(error.message || this.NETWORK_ERR);
      } finally {
        this.isSaving = false;
      }
    },

    // 创建或编辑答题卡。
    addQuestion(question) {
      const { options, tips } = AnswerCard.formatOptionsAndTipsToArray(question);
      return this.core.saveQuestion({
        questionId: question.questionId,
        name: question.name,
        type: question.type,
        isQuickQuestion: question.itemType === 1,
        answer: question.answer,
        options,
        tips,
      });
    },

    // 开始答题。
    async startQuestion(question, isAgain = false) {
      if (!this.canStart) {
        this.$iaMessage.default(this.cantStartTips);
        return;
      }
      if (this.ongoingQuestionId) {
        this.$iaMessage.default(this.t('plvIA.answerCard.ongoingTips'));
        return;
      }

      if (this.isStarting) return;
      this.isStarting = true;

      if (this.channelSetting.answerContentBlankEnabled) {
        for (const key in question) {
          if (/^option\d+$/.test(key) && question[key] === '') {
            question[key] = ' ';
          }
        }
      }

      try {
        const { options, tips } = AnswerCard.formatOptionsAndTipsToArray(question);
        const setting = {
          isAgain,
          duration: question.duration,
          defaultLimitTimeAnswer: this.channelSetting.defaultLimitTimeAnswer,
        };
        if (this.channelSetting.defaultLimitTimeAnswer) {
          setting.rightRank = 3;
        }
        const questionId = await this.core.start({
          question: {
            questionId: question.questionId,
            name: question.name,
            type: question.type,
            isQuickQuestion: question.itemType === 1,
            answer: question.answer,
            options,
            tips,
          },
          setting,
        });

        if (this.curQuestionId === questionId) {
          this.statistics = null;
        }
      } catch (error) {
        this.$iaMessage.default(error.message || this.NETWORK_ERR);
      } finally {
        this.isStarting = false;
      }
    },

    // 显示答题卡结果。
    showResult(questionId) {
      if (this.isInited) {
        this.handleChangeQuestion(questionId, true);
      } else {
        this.showResultId = questionId;
      }
      if (!this.sentList.includes(questionId)) {
        this.sentList.push(questionId);
      }
    },
  }
};
</script>

<style lang="scss">
@import '../../assets/styles/main.scss';
@import './style/theme-en.scss';

$blue: #0A8DFF;
.plv-ia-answer {
  height: 100%;
  font-size: 14px;
  position: relative;

  input, textarea {
    &:focus {
      outline: none;
    }
  }
}

.plv-ia-answer-tabs__header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  box-sizing: border-box;

  padding: 0 16px;
  margin: 0;
  font-size: 0;
  border-bottom: 1px solid #ECEFF1;
}

.plv-ia-answer-tabs__active-bar {
  width: 88px;
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background-color: $blue;
  z-index: 1;
  transition: transform .3s cubic-bezier(.645, .045, .355, 1);
  transform: translateX(16px);

  &--history {
    transform: translateX(88px + 16px);
  }

  &--log {
    transform: translateX(88px + 88px + 16px);
  }
}

.plv-ia-answer-tabs__nav {
  width: 88px;
  height: 48px;
  line-height: 48px;
  text-align: center;
  display: inline-block;
  cursor: pointer;
  user-select: none;
  font-size: 14px;

  &--active, &:hover {
    color: $blue;
  }
}

.plv-ia-answer-tabs__pane {
  display: none;
  height: 100%;
  padding-top: 48px;
  box-sizing: border-box;

  &--active {
    display: block;
  }
}

.plv-ia-answer-main {
  width: 100%;
  height: 100%;
  position: relative;
}

.plv-ia-answer-main__left {
  position: absolute;
  left: 0;
  top: 0;
  width: 200px;
  height: 100%;
  background: #EDF1F7;
}

.plv-ia-answer-main__right {
  padding-left: 200px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.plv-ia-answer-main__right__wrap {
  height: 100%;
  position: relative;
}

.plv-ia-answer-content__top {
  height: 100%;
  box-sizing: border-box;
  padding-bottom: 80px;
}

.plv-ia-answer-content__bottom {
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 24px;
  box-sizing: border-box;
}
.plv-ia-answer-version {
  font-size: 12px;
  color: #aaa;
  float: right;
  line-height: 48px;
}

.plv-ia-answer-log {
  padding: 20px;
}
.plv-ia-answer-log_list {
  margin: 10px 0;
  height: 400px;
  overflow: auto;
}
</style>
